import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Table, FormGroup, Spinner, Button } from "reactstrap";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/Users.css";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@mantis-sas/ui";

import {
  createMantisBusinessGroup,
  editMantisBusinessGroup,
  deleteMantisBusinessGroup,
  getMantisBusinessGroups,
  getMantisBusinessUsers,
  getSupervisors,
} from "../../../ApiCalls/mantisBusiness";

import { checkMantisBusinessUserPermission } from "../../../utils/businessUtils";
import { selectAuthState } from "../../../store/selectors/authSelectors";


function BusinessGroups(props) {
  const [mantisBusinessGroups, setMantisBusinessGroups] = useState([]);
  const [creatingNewGroup, setCreatingNewGroup] = useState(false);
  const [loadingCreatingNewGroup, setLoadingCreatingNewGroup] = useState(false);
  const [newGroupForm, setNewGroupForm] = useState({
    name: "",
    permissions: [],
    assignedUsers: [],
    supervisorId: "",
  });
  const [loading, setLoading] = useState(true);
  const [editingGroup, setEditingGroup] = useState(null);
  const [loadingEditingGroup, setLoadingEditingGroup] = useState(false);
  const [permissionsOptions, setPermissionsOptions] = useState([]);
  const [mantisBusinessUsers, setMantisBusinessUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [originalEditingGroup, setOriginalEditingGroup] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [expandedPermissions, setExpandedPermissions] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState(null);

  const getBusinessGroups = useCallback(async () => {
    try {
      const res = await getMantisBusinessGroups(
        props.mantisBusiness._id,
        props.token
      );
      if (res.success) {
        setMantisBusinessGroups(res.groups);
      } else {
        console.error("Error fetching groups:", res.message);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoading(false);
    }
  }, [props.mantisBusiness._id, props.token]);

  const getBusinessUsers = useCallback(async () => {
    try {
      const res = await getMantisBusinessUsers(
        props.mantisBusiness._id,
        props.token
      );
      if (res.success) {
        setMantisBusinessUsers(res.users);
        setUserOptions(
          res.users.map((user) => ({
            value: user._id,
            label: user.email,
          }))
        );
      } else {
        console.error("Error fetching users:", res.message);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [props.mantisBusiness._id, props.token]);

  const getSupervisorsList = useCallback(async () => {
    try {
      const res = await getSupervisors(props.mantisBusiness._id, props.token);
      if (res.success) {
        const options = res.supervisors.map((supervisor) => ({
          value: supervisor._id,
          label: `${supervisor.firstName} ${supervisor.lastName} - ${supervisor.position}`,
        }));
        setSupervisorOptions(options);
      } else {
        console.error("Error fetching supervisors:", res.message);
      }
    } catch (error) {
      console.error("Error fetching supervisors:", error);
    }
  }, [props.mantisBusiness._id, props.token]);

  useEffect(() => {
    const canViewGroups =
      props.user.mainUser ||
      checkMantisBusinessUserPermission(props.user, "VIEW_GROUPS") ||
      checkMantisBusinessUserPermission(props.user, "UPDATE_GROUPS") ||
      checkMantisBusinessUserPermission(props.user, "DELETE_GROUPS") ||
      checkMantisBusinessUserPermission(props.user, "CREATE_GROUPS");
    setHasPermission(canViewGroups);

    if (canViewGroups) {
      getBusinessGroups();
      getBusinessUsers();
      getSupervisorsList();
      setPermissionsOptions(
        props.mantisBusiness.enabledPermissions.map((permission) => ({
          value: permission.permission,
          label: permission.friendlyName,
        }))
      );
    }
  }, [
    props.user,
    props.mantisBusiness.enabledPermissions,
    getBusinessGroups,
    getBusinessUsers,
    getSupervisorsList,
  ]);

  useEffect(() => {
    const loadSupervisors = async () => {
      try {
        const res = await getSupervisors(props.mantisBusiness._id, props.token);
        if (res.success) {
          setSupervisors(res.supervisors);
          setSupervisorOptions(
            res.supervisors.map((supervisor) => ({
              value: supervisor._id,
              label: `${supervisor.firstName} ${supervisor.lastName} - ${supervisor.position}`,
              original: supervisor,
            }))
          );
        } else {
          toast.error("Error al cargar supervisores: " + res.message);
        }
      } catch (error) {
        console.error("Error loading supervisors:", error);
        toast.error("Error al cargar supervisores");
      }
    };

    // L-01 (MA-06): not gated on props.token — see BusinessUsers. Left gated, the
    // supervisors list stayed empty.
    if (props.mantisBusiness._id) {
      loadSupervisors();
    }
  }, [props.mantisBusiness._id, props.token]);

  useEffect(() => {
    if (newGroupForm.name.length > 0) {
      setErrors({});
    } else {
      setErrors((prev) => ({
        ...prev,
        name: "El nombre del grupo es requerido",
      }));
    }
  }, [newGroupForm]);

  const validateForm = (form) => {
    const newErrors = {};
    if (!form.name) newErrors.name = "Debe ingresar un nombre para el grupo";
    if (!form.permissions || form.permissions.length === 0)
      newErrors.permissions = "Debe seleccionar al menos un permiso";
    if (!form.assignedUsers || form.assignedUsers.length === 0)
      newErrors.assignedUsers = "Debe asignar al menos un usuario";

    if (form === newGroupForm) {
      if (!form.supervisorId)
        newErrors.supervisor = "Debe seleccionar un supervisor";
    } else {
      if (!form.supervisor?._id)
        newErrors.supervisor = "Debe seleccionar un supervisor";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateNewGroup = async (event) => {
    event.preventDefault();
    if (!validateForm(newGroupForm)) return;

    setLoadingCreatingNewGroup(true);
    try {
      const assignedUsers = newGroupForm.assignedUsers.map((userId) => {
        const user = mantisBusinessUsers.find((u) => u._id === userId);
        return {
          userId: user._id,
          userName: user.email,
        };
      });
      const res = await createMantisBusinessGroup(
        props.mantisBusiness._id,
        props.token,
        { ...newGroupForm, assignedUsers },
        newGroupForm.supervisorId
      );
      if (res.success) {
        setMantisBusinessGroups([...mantisBusinessGroups, res.group]);
        setCreatingNewGroup(false);
        setNewGroupForm({
          name: "",
          permissions: [],
          assignedUsers: [],
          supervisorId: "",
        });
        toast.success("Grupo creado exitosamente");
      } else {
        toast.error(
          "Error al crear el grupo: " + (res.message || "Error desconocido")
        );
      }
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error(
        "Error al crear el grupo: " + (error.message || "Error desconocido")
      );
    } finally {
      setLoadingCreatingNewGroup(false);
    }
  };

  const handleEditGroup = async (event) => {
    event.preventDefault();
    if (!validateForm(editingGroup)) return;

    if (JSON.stringify(editingGroup) === JSON.stringify(originalEditingGroup)) {
      toast.warning("No se han realizado cambios en el grupo");
      return;
    }

    if (window.confirm("¿Está seguro de que desea realizar estos cambios?")) {
      setLoadingEditingGroup(true);
      try {
        const groupData = {
          name: editingGroup.name,
          permissions: editingGroup.permissions,
        };

        const assignedUsers = editingGroup.assignedUsers.map(
          (user) => user.userId
        );

        const res = await editMantisBusinessGroup(
          props.mantisBusiness._id,
          editingGroup._id,
          groupData,
          editingGroup.supervisor?._id,
          assignedUsers,
          props.token
        );

        if (res.success) {
          toast.success("Grupo actualizado exitosamente");
          const updatedGroups = mantisBusinessGroups.map((group) =>
            group._id === res.group._id ? res.group : group
          );
          setMantisBusinessGroups(updatedGroups);
          setEditingGroup(null);
          setOriginalEditingGroup(null);
        } else {
          toast.error(
            "Error al editar el grupo: " + (res.message || "Error desconocido")
          );
        }
      } catch (error) {
        console.error("Error editing group:", error);
        toast.error(
          "Error al editar el grupo: " + (error.message || "Error desconocido")
        );
      } finally {
        setLoadingEditingGroup(false);
      }
    }
  };

  const setEditingGroupWithOriginal = (group) => {
    console.log("Editing group:", group);
    setEditingGroup(group);
    setOriginalEditingGroup(JSON.parse(JSON.stringify(group)));
  };

  async function handleDeleteGroup(groupId) {
    if (window.confirm("¿Está seguro de que desea eliminar este grupo?")) {
      try {
        const res = await deleteMantisBusinessGroup(
          props.mantisBusiness._id,
          groupId,
          props.token
        );
        if (res.success) {
          setMantisBusinessGroups(
            mantisBusinessGroups.filter((group) => group._id !== groupId)
          );
          toast.success("Grupo eliminado exitosamente");
        } else {
          toast.error(
            "Error al eliminar el grupo: " +
              (res.message || "Error desconocido")
          );
        }
      } catch (error) {
        console.error("Error deleting group:", error);
        toast.error(
          "Error al eliminar el grupo: " +
            (error.message || "Error desconocido")
        );
      }
    }
  }

  function handleInputChange(event, setStateFunction) {
    setStateFunction((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  }

  
  const getSupervisorName = (supervisor) => {
    if (!supervisor) return "No asignado";
    return `${supervisor.firstName} ${supervisor.lastName}${
      supervisor.position ? ` - ${supervisor.position}` : ""
    }`;
  };

  const formatPermissions = (permissions, groupId) => {
    if (!permissions || permissions.length === 0) return "-";

    const formattedPermissions = permissions.map((p) => {
      const permission = typeof p === "string" ? p : p.permission;
      const foundPermission = props.mantisBusiness.enabledPermissions.find(
        (ep) => ep.permission === permission
      );
      return foundPermission ? foundPermission.friendlyName : permission;
    });

    const isExpanded = expandedPermissions === groupId;

    return (
      <div className="permissions-cell">
        <div className={`permissions-list ${isExpanded ? "expanded" : ""}`}>
          {isExpanded ? (
            formattedPermissions.map((permission, index) => (
              <div key={index} className="permission-item">
                {permission}
              </div>
            ))
          ) : (
            <>
              {formattedPermissions.slice(0, 2).join(", ")}
              {formattedPermissions.length > 2 && "..."}
            </>
          )}
        </div>
        {formattedPermissions.length > 2 && (
          <button
            className="view-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedPermissions(isExpanded ? null : groupId);
            }}
          >
            {isExpanded ? "Ver menos" : "Ver más"}
          </button>
        )}
      </div>
    );
  };

  const formatUsers = (users, groupId) => {
    if (!users || users.length === 0) return "-";

    const isExpanded = expandedUsers === groupId;

    return (
      <div className="users-cell">
        <div className={`users-list ${isExpanded ? "expanded" : ""}`}>
          {isExpanded ? (
            users.map((user, index) => (
              <div key={index} className="permission-item">
                {user.userName}
              </div>
            ))
          ) : (
            <>
              {users
                .slice(0, 2)
                .map((u) => u.userName)
                .join(", ")}
              {users.length > 2 && "..."}
            </>
          )}
        </div>
        {users.length > 2 && (
          <button
            className="view-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedUsers(isExpanded ? null : groupId);
            }}
          >
            {isExpanded ? "Ver menos" : "Ver más"}
          </button>
        )}
      </div>
    );
  };

  const renderGroupModal = (isEditing) => {
    const formData = isEditing ? editingGroup : newGroupForm;
    const loading = isEditing ? loadingEditingGroup : loadingCreatingNewGroup;
    const isOpen = creatingNewGroup || !!editingGroup;

    const handleSubmit = (e) => {
      e.preventDefault();
      if (isEditing) {
        handleEditGroup(e);
      } else {
        handleCreateNewGroup(e);
      }
    };

    const handleClose = () => {
      setEditingGroup(null);
      setOriginalEditingGroup(null);
      setErrors({});
      setCreatingNewGroup(false);
    };

    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="text-slate-900 dark:text-slate-100">
                {isEditing ? "Editar Grupo" : "Crear Nuevo Grupo"}
              </span>
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Modifica la información del grupo" : "Completa los datos para crear un nuevo grupo"}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Form onSubmit={handleSubmit}>
              <div className="space-y-8">
                {/* Basic Information Section */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-2 pb-2 border-b border-slate-200/50 dark:border-slate-700/50">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                      Información Básica
                    </h4>
                  </div>

                  <FormGroup className="space-y-2">
                    <Form.Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Nombre del Grupo *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange(
                          e,
                          isEditing ? setEditingGroup : setNewGroupForm
                        )
                      }
                      isInvalid={!!errors.name}
                      className="w-full px-4 py-3 bg-white/50 dark:bg-slate-700/50 border border-slate-300/50 dark:border-slate-600/50 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-slate-900 dark:text-slate-100"
                      placeholder="Ingresa el nombre del grupo"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup className="space-y-2">
                    <Form.Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Supervisor *
                    </Form.Label>
                    <Select
                      options={supervisorOptions}
                      value={supervisorOptions.find((option) =>
                        isEditing
                          ? option.value === editingGroup.supervisor?.userId
                          : option.value === newGroupForm.supervisorId
                      )}
                      onChange={(selectedOption) => {
                        if (isEditing) {
                          setEditingGroup((prev) => ({
                            ...prev,
                            supervisor: selectedOption
                              ? supervisors.find(
                                  (s) => s._id === selectedOption.value
                                )
                              : null,
                          }));
                        } else {
                          setNewGroupForm((prev) => ({
                            ...prev,
                            supervisorId: selectedOption
                              ? selectedOption.value
                              : null,
                          }));
                        }
                      }}
                      placeholder="Seleccionar supervisor..."
                      isClearable
                      className="react-select-clean"
                      classNamePrefix="select"
                    />
                    {errors.supervisor && (
                      <div className="text-red-500 text-sm mt-1">
                        {errors.supervisor}
                      </div>
                    )}
                  </FormGroup>
                </div>

                {/* Permissions Section */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-2 pb-2 border-b border-slate-200/50 dark:border-slate-700/50">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                      Permisos del Grupo
                    </h4>
                  </div>

                  <FormGroup className="space-y-2">
                    <Form.Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Permisos *
                    </Form.Label>
                    <Select
                      isMulti
                      options={permissionsOptions}
                      value={permissionsOptions.filter((option) =>
                        isEditing
                          ? editingGroup.permissions.includes(option.value)
                          : newGroupForm.permissions.includes(option.value)
                      )}
                      onChange={(selectedOptions) => {
                        const permissions = selectedOptions.map(
                          (option) => option.value
                        );
                        if (isEditing) {
                          setEditingGroup((prev) => ({
                            ...prev,
                            permissions,
                          }));
                        } else {
                          setNewGroupForm((prev) => ({
                            ...prev,
                            permissions,
                          }));
                        }
                      }}
                      placeholder="Seleccionar permisos..."
                      className="react-select-clean"
                      classNamePrefix="select"
                      closeMenuOnSelect={false}
                    />
                    {errors.permissions && (
                      <div className="text-red-500 text-sm mt-1">
                        {errors.permissions}
                      </div>
                    )}
                  </FormGroup>
                </div>

                {/* Users Section */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-2 pb-2 border-b border-slate-200/50 dark:border-slate-700/50">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                    <h4 className="text-lg font-medium text-slate-900 dark:text-slate-100">
                      Usuarios Asignados
                    </h4>
                  </div>

                  <FormGroup className="space-y-2">
                    <Form.Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Usuarios *
                    </Form.Label>
                    <Select
                      isMulti
                      options={userOptions}
                      value={userOptions.filter((option) =>
                        isEditing
                          ? editingGroup.assignedUsers
                              .map((user) => user.userId)
                              .includes(option.value)
                          : newGroupForm.assignedUsers.includes(option.value)
                      )}
                      onChange={(selectedOptions) => {
                        let assignedUsers = [];
                        if (!isEditing) {
                          assignedUsers = selectedOptions.map(
                            (option) => option.value
                          );
                        } else {
                          assignedUsers = selectedOptions.map((option) => {
                            const user = mantisBusinessUsers.find(
                              (u) => u._id === option.value
                            );
                            return {
                              userId: user._id,
                              userName: user.email,
                            };
                          });
                        }
                        console.log("Assigned users:", assignedUsers);
                        if (isEditing) {
                          setEditingGroup((prev) => ({
                            ...prev,
                            assignedUsers,
                          }));
                        } else {
                          setNewGroupForm((prev) => ({
                            ...prev,
                            assignedUsers,
                          }));
                        }
                      }}
                      placeholder="Seleccionar usuarios..."
                      className="react-select-clean"
                      classNamePrefix="select"
                      closeMenuOnSelect={false}
                    />
                    {errors.assignedUsers && (
                      <div className="text-red-500 text-sm mt-1">
                        {errors.assignedUsers}
                      </div>
                    )}
                  </FormGroup>
                </div>

              </div>
            </Form>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white/50 dark:bg-slate-700/50 border border-slate-300/50 dark:border-slate-600/50 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-xl shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  <span>Procesando...</span>
                </>
              ) : (
                <span>{isEditing ? "Guardar Cambios" : "Crear Grupo"}</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  if (!hasPermission) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 8.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-red-800 dark:text-red-200 font-medium">Acceso Denegado</h3>
              <p className="text-red-600 dark:text-red-300 text-sm">No tienes permiso para ver esta página.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {!creatingNewGroup && !editingGroup && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-sm">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Grupos del Sistema
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {mantisBusinessGroups.length} grupos registrados
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setCreatingNewGroup(true)}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-xl shadow-sm transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Crear Grupo</span>
          </button>
        </div>
      )}



      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <Spinner className="text-indigo-600" />
            <p className="text-slate-600 dark:text-slate-400">Cargando grupos...</p>
          </div>
        </div>
      ) : (
        <>
          {!creatingNewGroup && !editingGroup && mantisBusinessGroups.length > 0 && (
            <div className="bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="w-full">
                  <thead className="bg-slate-50/50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Permisos
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Fecha de Creación
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Usuarios
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Supervisor
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/50 dark:divide-slate-700/50">
                    {mantisBusinessGroups.map((group) => (
                      <tr key={group._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                              {group.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {group.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {formatPermissions(group.permissions, group._id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                          {new Date(group.dateCreated).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {formatUsers(group.assignedUsers, group._id)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                          {getSupervisorName(group.supervisor)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setEditingGroupWithOriginal(group)}
                              className="p-2 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(group._id)}
                              className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {renderGroupModal(!!editingGroup)}

          {!creatingNewGroup && !editingGroup && mantisBusinessGroups.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                No hay grupos
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Comienza creando tu primer grupo del sistema.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setCreatingNewGroup(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-xl shadow-sm transition-all"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Crear Grupo
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default connect(selectAuthState)(BusinessGroups);